import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '@/context/AuthContext';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles, Loader2, X, MapPin, Camera, ChevronRight, ChevronDown,
  Navigation, Clock, Euro, Check, RefreshCw, ExternalLink, Bookmark, BookmarkCheck, Headphones, Volume2, Info, MoreHorizontal, Filter, ToggleLeft, ToggleRight
} from 'lucide-react';
import { generateQuickPlan, addFavorite, removeFavorite, checkFavorite } from '@/lib/api';
import { FEATURE_FLAGS } from './config';
import InstantTools from '@/components/InstantTools';

// Main desires with fun emojis - 5 main + 1 "Autres" with sub-options
const DESIRES = [
  { id: 'visit', icon: '🏛️', label: { fr: 'Visiter', en: 'Visit' } },
  { id: 'discover', icon: '🔍', label: { fr: 'Découvrir', en: 'Discover' } },
  { id: 'eat', icon: '🍽️', label: { fr: 'Manger', en: 'Eat' } },
  { id: 'drink', icon: '🍹', label: { fr: 'Boire', en: 'Drink' } },
  { id: 'party', icon: '🎉', label: { fr: 'Sortir', en: 'Party' } },
  { id: 'other', icon: '✨', label: { fr: 'Autres', en: 'Other' }, hasSubOptions: true },
];

// Repensé: Sub-options for "Autres" avec questions contextuelles
const OTHER_OPTIONS = [
  { id: 'chill', icon: '☀️', label: { fr: 'Chiller', en: 'Chill' }, 
    question: { fr: 'Où veux-tu te poser ?', en: 'Where do you want to relax?' } },
  { id: 'sport', icon: '🏃', label: { fr: 'Bouger', en: 'Move' },
    question: { fr: 'Quel type d\'activité ?', en: 'What type of activity?' },
    subOptions: [
      { id: 'sport_outdoor', icon: '🌳', label: { fr: 'En plein air', en: 'Outdoor' } },
      { id: 'sport_indoor', icon: '🏋️', label: { fr: 'En salle', en: 'Indoor' } },
      { id: 'sport_water', icon: '🏊', label: { fr: 'Aquatique', en: 'Water' } },
    ]
  },
  { id: 'shopping', icon: '🛍️', label: { fr: 'Shopping', en: 'Shopping' },
    question: { fr: 'Qu\'est-ce qui te ferait plaisir ?', en: 'What are you looking for?' },
    subOptions: [
      { id: 'shopping_souvenirs', icon: '🎁', label: { fr: 'Souvenirs', en: 'Souvenirs' } },
      { id: 'shopping_fashion', icon: '👗', label: { fr: 'Mode', en: 'Fashion' } },
      { id: 'shopping_vintage', icon: '📿', label: { fr: 'Vintage / Brocante', en: 'Vintage' } },
      { id: 'shopping_local', icon: '🧺', label: { fr: 'Produits locaux', en: 'Local products' } },
    ]
  },
  { id: 'culture', icon: '🎭', label: { fr: 'Culture', en: 'Culture' },
    question: { fr: 'Quel genre de culture ?', en: 'What kind of culture?' },
    subOptions: [
      { id: 'culture_art', icon: '🖼️', label: { fr: 'Art & Expos', en: 'Art & Exhibits' } },
      { id: 'culture_history', icon: '📜', label: { fr: 'Histoire', en: 'History' } },
      { id: 'culture_show', icon: '🎪', label: { fr: 'Spectacles', en: 'Shows' } },
    ]
  },
  { id: 'nature', icon: '🌳', label: { fr: 'Nature', en: 'Nature' } },
  { id: 'wellness', icon: '💆', label: { fr: 'Bien-être', en: 'Wellness' },
    question: { fr: 'Qu\'est-ce qui te détendrait ?', en: 'What would relax you?' },
    subOptions: [
      { id: 'wellness_spa', icon: '🧖', label: { fr: 'Spa / Hammam', en: 'Spa' } },
      { id: 'wellness_massage', icon: '💆', label: { fr: 'Massage', en: 'Massage' } },
      { id: 'wellness_yoga', icon: '🧘', label: { fr: 'Yoga / Méditation', en: 'Yoga' } },
    ]
  },
];

// Constraint options - simple toggles
const CONSTRAINTS = {
  distance: [
    { id: 'near', icon: '🚶', value: 500, label: { fr: '5 min', en: '5 min' } },
    { id: 'medium', icon: '🚲', value: 2000, label: { fr: '15 min', en: '15 min' } },
    { id: 'far', icon: '🚗', value: 5000, label: { fr: '30 min', en: '30 min' } },
  ],
  time: [
    { id: 'quick', icon: '⚡', value: 30, label: { fr: '30 min', en: '30 min' } },
    { id: 'hour', icon: '⏰', value: 60, label: { fr: '1 heure', en: '1 hour' } },
    { id: 'half_day', icon: '🌤️', value: 180, label: { fr: '3 heures', en: '3 hours' } },
  ],
};

// Budget options adapted per desire type - AMÉLIORÉ
const BUDGET_BY_DESIRE = {
  visit: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '💵', value: 'cheap', label: { fr: '< 15€', en: '< $15' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  discover: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '💵', value: 'cheap', label: { fr: '< 10€', en: '< $10' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  eat: [
    { id: 'cheap', icon: '🍜', value: 'cheap', label: { fr: '< 15€', en: '< $15' } },
    { id: 'medium', icon: '🍽️', value: 'medium', label: { fr: '15-30€', en: '$15-30' } },
    { id: 'any', icon: '🥂', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  drink: [
    { id: 'cheap', icon: '🍺', value: 'cheap', label: { fr: '< 10€', en: '< $10' } },
    { id: 'medium', icon: '🍸', value: 'medium', label: { fr: '10-20€', en: '$10-20' } },
    { id: 'any', icon: '🥂', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  party: [
    { id: 'cheap', icon: '🎫', value: 'cheap', label: { fr: '< 15€', en: '< $15' } },
    { id: 'medium', icon: '🎉', value: 'medium', label: { fr: '15-30€', en: '$15-30' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  chill: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '☕', value: 'cheap', label: { fr: '< 10€', en: '< $10' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  // Sport amélioré - inclut options gratuites
  sport: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit (parc, etc.)', en: 'Free (park, etc.)' } },
    { id: 'cheap', icon: '💵', value: 'cheap', label: { fr: '< 20€ (salle)', en: '< $20 (gym)' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  sport_outdoor: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  sport_indoor: [
    { id: 'cheap', icon: '💵', value: 'cheap', label: { fr: '< 15€', en: '< $15' } },
    { id: 'medium', icon: '🏋️', value: 'medium', label: { fr: '15-30€', en: '$15-30' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  // Shopping amélioré - basé sur envies pas durée
  shopping: [
    { id: 'cheap', icon: '🛍️', value: 'cheap', label: { fr: '< 50€', en: '< $50' } },
    { id: 'medium', icon: '💳', value: 'medium', label: { fr: '50-150€', en: '$50-150' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  shopping_souvenirs: [
    { id: 'cheap', icon: '🎁', value: 'cheap', label: { fr: '< 20€', en: '< $20' } },
    { id: 'medium', icon: '💝', value: 'medium', label: { fr: '20-50€', en: '$20-50' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  shopping_fashion: [
    { id: 'cheap', icon: '👕', value: 'cheap', label: { fr: '< 100€', en: '< $100' } },
    { id: 'medium', icon: '👗', value: 'medium', label: { fr: '100-300€', en: '$100-300' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Luxe / Peu importe', en: 'Luxury / Any' } },
  ],
  culture: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '🎫', value: 'cheap', label: { fr: '< 20€', en: '< $20' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  nature: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '🌿', value: 'cheap', label: { fr: '< 10€', en: '< $10' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  wellness: [
    { id: 'cheap', icon: '💆', value: 'cheap', label: { fr: '< 30€', en: '< $30' } },
    { id: 'medium', icon: '🧖', value: 'medium', label: { fr: '30-80€', en: '$30-80' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
  other: [
    { id: 'free', icon: '🆓', value: 'free', label: { fr: 'Gratuit', en: 'Free' } },
    { id: 'cheap', icon: '💵', value: 'cheap', label: { fr: '< 20€', en: '< $20' } },
    { id: 'any', icon: '💎', value: 'any', label: { fr: 'Peu importe', en: 'Any' } },
  ],
};

// Flow steps
const STEPS = {
  DESIRE: 0,
  OTHER_OPTIONS: 0.5, // Sub-step for "Autres" selection
  SUB_OPTIONS: 0.75, // Sub-sub-options (e.g., sport type, shopping type)
  CONSTRAINTS: 1,
  RESULTS: 2,
};

export function QuickDeckGame({ onScanHere, onAudioGuide, onClose }) {
  const { language } = useTranslation();
  const [step, setStep] = useState(STEPS.DESIRE);
  const [desire, setDesire] = useState(null);
  const [subDesire, setSubDesire] = useState(null); // For sub-categories like sport_outdoor
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [showSubOptions, setShowSubOptions] = useState(false);
  const [currentOtherOption, setCurrentOtherOption] = useState(null);
  const [selectedInfoResult, setSelectedInfoResult] = useState(null); // For quick info popup
  const [constraints, setConstraints] = useState({
    distance: 'medium',
    budget: 'any',
    time: 'hour',
  });
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  
  // Filtre "Ouvert actuellement"
  const [filterOpenOnly, setFilterOpenOnly] = useState(false);
  
  // Favoris state
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [savingFavorite, setSavingFavorite] = useState(null);
  
  // Use global audio player context
  const { startAudioGuide, isGenerating } = useAudioPlayer();

  // Version picker state (set to a result when user taps audio guide button)
  const [versionPickerResult, setVersionPickerResult] = useState(null);

  // Get budget options based on current desire or sub-desire
  const budgetOptions = BUDGET_BY_DESIRE[subDesire] || BUDGET_BY_DESIRE[desire] || BUDGET_BY_DESIRE.discover;
  
  // Apply open filter when results or filter changes
  useEffect(() => {
    if (filterOpenOnly) {
      setFilteredResults(results.filter(r => r.open_now === true));
    } else {
      setFilteredResults(results);
    }
  }, [results, filterOpenOnly]);

  // Function to request location with multiple strategies
  const requestLocation = useCallback(() => {
    setLocationLoading(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError(language === 'fr' ? 'Géolocalisation non supportée' : 'Geolocation not supported');
      setLocationLoading(false);
      return;
    }

    // Strategy 1: Try high accuracy first (GPS)
    const tryHighAccuracy = () => {
      console.log('[QuickDeck] Trying high accuracy GPS...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setUserLocation(loc);
          setLocationLoading(false);
          console.log('[QuickDeck] GPS location:', loc.lat, loc.lng, 'accuracy:', loc.accuracy, 'm');
        },
        (error) => {
          console.warn('[QuickDeck] High accuracy failed:', error.code, error.message);
          // Try low accuracy as fallback
          tryLowAccuracy();
        },
        { 
          enableHighAccuracy: true, 
          timeout: 8000,
          maximumAge: 30000
        }
      );
    };

    // Strategy 2: Low accuracy (Network/WiFi based)
    const tryLowAccuracy = () => {
      console.log('[QuickDeck] Trying low accuracy (network)...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setUserLocation(loc);
          setLocationLoading(false);
          console.log('[QuickDeck] Network location:', loc.lat, loc.lng, 'accuracy:', loc.accuracy, 'm');
        },
        (error) => {
          console.warn('[QuickDeck] Low accuracy failed:', error.code, error.message);
          handleLocationError(error);
        },
        { 
          enableHighAccuracy: false, 
          timeout: 10000,
          maximumAge: 60000
        }
      );
    };

    // Handle final error
    const handleLocationError = (error) => {
      let errorMsg = language === 'fr' ? 'Position non disponible' : 'Location unavailable';
      if (error.code === 1) {
        errorMsg = language === 'fr' ? 'Permission refusée — activez la localisation' : 'Permission denied — enable location';
      } else if (error.code === 2) {
        errorMsg = language === 'fr' ? 'Position indisponible — vérifiez votre connexion' : 'Position unavailable — check connection';
      } else if (error.code === 3) {
        errorMsg = language === 'fr' ? 'Délai dépassé — réessayez' : 'Timeout — try again';
      }
      setLocationError(errorMsg);
      setLocationLoading(false);
    };

    // Start with high accuracy
    tryHighAccuracy();
  }, [language]);

  // Watch position for continuous updates (optional enhancement)
  const watchLocationRef = useRef(null);
  
  useEffect(() => {
    // Cleanup watch on unmount
    return () => {
      if (watchLocationRef.current) {
        navigator.geolocation.clearWatch(watchLocationRef.current);
      }
    };
  }, []);

  // Get user location on mount
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  // Handle desire selection - auto advance or show sub-options for "Autres"
  const selectDesire = useCallback((desireId) => {
    const desireConfig = DESIRES.find(d => d.id === desireId);
    
    if (desireConfig?.hasSubOptions) {
      // Show "Autres" sub-options
      setShowOtherOptions(true);
      return;
    }
    
    setDesire(desireId);
    setSubDesire(null);
    setShowOtherOptions(false);
    setShowSubOptions(false);
    // Reset budget to default 'any' or first option for this desire type
    const desireBudgets = BUDGET_BY_DESIRE[desireId] || BUDGET_BY_DESIRE.discover;
    setConstraints(prev => ({ 
      ...prev, 
      budget: desireBudgets[desireBudgets.length - 1].id // Default to "Peu importe"
    }));
    // Small delay for visual feedback then advance
    setTimeout(() => setStep(STEPS.CONSTRAINTS), 200);
  }, []);

  // Handle "Autres" sub-option selection - may have sub-sub options
  const selectOtherOption = useCallback((optionId) => {
    const option = OTHER_OPTIONS.find(o => o.id === optionId);
    
    // If option has sub-options (e.g., sport, shopping), show them
    if (option?.subOptions) {
      setCurrentOtherOption(option);
      setShowOtherOptions(false);
      setShowSubOptions(true);
      return;
    }
    
    setDesire(optionId);
    setSubDesire(null);
    setShowOtherOptions(false);
    setShowSubOptions(false);
    const desireBudgets = BUDGET_BY_DESIRE[optionId] || BUDGET_BY_DESIRE.other;
    setConstraints(prev => ({ 
      ...prev, 
      budget: desireBudgets[desireBudgets.length - 1].id
    }));
    setTimeout(() => setStep(STEPS.CONSTRAINTS), 200);
  }, []);
  
  // Handle sub-sub-option selection (e.g., sport_outdoor, shopping_souvenirs)
  const selectSubOption = useCallback((subOptionId) => {
    const parentId = currentOtherOption?.id || 'other';
    setDesire(parentId);
    setSubDesire(subOptionId);
    setShowSubOptions(false);
    setCurrentOtherOption(null);
    
    const desireBudgets = BUDGET_BY_DESIRE[subOptionId] || BUDGET_BY_DESIRE[parentId] || BUDGET_BY_DESIRE.other;
    setConstraints(prev => ({ 
      ...prev, 
      budget: desireBudgets[desireBudgets.length - 1].id
    }));
    setTimeout(() => setStep(STEPS.CONSTRAINTS), 200);
  }, [currentOtherOption]);

  // Handle constraint selection
  const selectConstraint = useCallback((type, value) => {
    setConstraints(prev => ({ ...prev, [type]: value }));
  }, []);

  // Handle audio guide for a result - Opens version picker first
  const handleAudioGuide = useCallback((result) => {
    setVersionPickerResult(result);
  }, []);

  // Called when user picks a version in the picker
  const handleVersionSelect = useCallback(async (version) => {
    if (!versionPickerResult) return;
    const place = versionPickerResult;
    setVersionPickerResult(null);
    await startAudioGuide({
      name: place.name,
      category: place.category || '',
      description: place.description || '',
      address: place.address || '',
      lat: place.lat,
      lng: place.lng,
      place_id: place.place_id || place.id,
    }, { version, lang: language });
  }, [versionPickerResult, language, startAudioGuide]);

  // Handle quick info popup for a result
  const handleQuickInfo = useCallback((result) => {
    setSelectedInfoResult(result);
  }, []);
  
  // Handle favorite toggle
  const handleToggleFavorite = useCallback(async (result) => {
    const placeId = result.place_id || result.id;
    setSavingFavorite(placeId);
    
    try {
      if (favoriteIds.has(placeId)) {
        // Remove from favorites
        await removeFavorite(placeId);
        setFavoriteIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(placeId);
          return newSet;
        });
      } else {
        // Add to favorites
        await addFavorite({
          place_id: placeId,
          name: result.name,
          category: result.category || '',
          address: result.address || '',
          description: result.description || '',
          rating: result.rating,
          photo: result.photo,
          lat: result.lat,
          lng: result.lng,
          source: 'instant_pulse'
        });
        setFavoriteIds(prev => new Set([...prev, placeId]));
      }
    } catch (err) {
      console.error('[QuickDeck] Favorite error:', err);
    } finally {
      setSavingFavorite(null);
    }
  }, [favoriteIds]);

  // Generate results with location
  const generate = useCallback(async () => {
    setIsLoading(true);
    setStep(STEPS.RESULTS);
    setFilterOpenOnly(false); // Reset filter
    
    const distanceOption = CONSTRAINTS.distance.find(d => d.id === constraints.distance);
    const budgetOption = budgetOptions.find(b => b.id === constraints.budget);
    const timeOption = CONSTRAINTS.time.find(t => t.id === constraints.time);
    
    // Use sub-desire if available for more specific results
    const effectiveDesire = subDesire || desire;
    
    try {
      const params = {
        desire: effectiveDesire,
        language: language || 'fr',  // 🔧 Send user's language
        constraints: {
          max_distance: distanceOption?.value || 2000,
          budget_level: budgetOption?.value || 'any',
          duration_minutes: timeOption?.value || 60,
        },
        // Add location if available
        ...(userLocation && {
          location: {
            lat: userLocation.lat,
            lng: userLocation.lng
          }
        })
      };
      console.log('[QuickDeck] Generating with params:', params);
      const response = await generateQuickPlan(params);
      setResults(response.data?.results || getMockResults(effectiveDesire, language, userLocation));
    } catch (err) {
      console.error('[QuickDeck] Error:', err);
      setResults(getMockResults(effectiveDesire, language, userLocation));
    } finally {
      setIsLoading(false);
    }
  }, [desire, subDesire, constraints, language, userLocation, budgetOptions]);

  // Reset and start over
  const reset = useCallback(() => {
    setStep(STEPS.DESIRE);
    setDesire(null);
    setSubDesire(null);
    setResults([]);
    setFilteredResults([]);
    setShowOtherOptions(false);
    setShowSubOptions(false);
    setCurrentOtherOption(null);
    setSelectedInfoResult(null);
    setFilterOpenOnly(false);
  }, []);

  // Go back one step
  const goBack = useCallback(() => {
    if (showSubOptions) {
      setShowSubOptions(false);
      setShowOtherOptions(true);
      setCurrentOtherOption(null);
    } else if (showOtherOptions) {
      setShowOtherOptions(false);
    } else if (step === STEPS.CONSTRAINTS) {
      setStep(STEPS.DESIRE);
      setDesire(null);
      setSubDesire(null);
    } else if (step === STEPS.RESULTS) {
      setStep(STEPS.CONSTRAINTS);
    }
  }, [step, showOtherOptions, showSubOptions]);

  if (!FEATURE_FLAGS.quickdeck) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 100 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      data-testid="quick-deck-game"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Card */}
      <motion.div
        layout
        className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-1 bg-purple-100">
          <motion.div 
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
            initial={{ width: '33%' }}
            animate={{ width: step === STEPS.DESIRE ? '33%' : step === STEPS.CONSTRAINTS ? '66%' : '100%' }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-100">
          <div className="flex items-center gap-3">
            {(step > STEPS.DESIRE || showOtherOptions) && (
              <button 
                onClick={goBack}
                className="p-1.5 -ml-1.5 rounded-lg hover:bg-purple-50 text-purple-600"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
            )}
            <div className="flex-1">
              <h2 className="font-bold text-purple-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-500" />
                {language === 'fr' ? 'Instant Pulse' : 'Instant Pulse'}
              </h2>
              <p className="text-xs text-purple-500">
                {step === STEPS.DESIRE && !showOtherOptions && (language === 'fr' ? "Qu'est-ce qui te ferait plaisir ?" : 'What would you like?')}
                {showOtherOptions && (language === 'fr' ? 'Que veux-tu faire ?' : 'What do you want to do?')}
                {step === STEPS.CONSTRAINTS && (language === 'fr' ? 'Quelques préférences...' : 'A few preferences...')}
                {step === STEPS.RESULTS && (language === 'fr' ? 'Voilà pour toi !' : 'Here you go!')}
              </p>
            </div>
            {/* Location indicator - clickable to retry */}
            <button
              onClick={!userLocation && !locationLoading ? requestLocation : undefined}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                locationLoading 
                  ? 'bg-purple-100 text-purple-600'
                  : userLocation 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer'
              }`}
              title={!userLocation && !locationLoading ? (language === 'fr' ? 'Cliquer pour réessayer' : 'Click to retry') : ''}
            >
              {locationLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <MapPin className="w-3 h-3" />
              )}
              {locationLoading 
                ? (language === 'fr' ? '...' : '...')
                : userLocation 
                  ? (language === 'fr' ? 'GPS ✓' : 'GPS ✓') 
                  : (language === 'fr' ? 'Non localisé' : 'No GPS')}
            </button>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-purple-50 text-purple-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Desire Selection */}
          {step === STEPS.DESIRE && !showOtherOptions && !showSubOptions && (
            <motion.div
              key="desire"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="p-5"
            >
              {/* Location status message */}
              {!locationLoading && !userLocation && (
                <button 
                  onClick={requestLocation}
                  className="mb-4 w-full px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-xs flex items-center justify-between gap-2 hover:bg-amber-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {locationError || (language === 'fr' 
                        ? 'Localisation requise pour des résultats précis' 
                        : 'Location needed for accurate results')}
                    </span>
                  </div>
                  <span className="text-amber-600 font-medium">
                    {language === 'fr' ? 'Activer →' : 'Enable →'}
                  </span>
                </button>
              )}
              
              {locationLoading && (
                <div className="mb-4 px-3 py-2.5 rounded-xl bg-purple-50 border border-purple-200 text-purple-600 text-xs flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  <span>
                    {language === 'fr' ? 'Récupération de votre position...' : 'Getting your location...'}
                  </span>
                </div>
              )}
              
              {userLocation && (
                <div className="mb-4 px-3 py-2 rounded-xl bg-green-50 border border-green-200 text-green-700 text-xs flex items-center gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {language === 'fr' 
                      ? '📍 Position détectée — résultats autour de vous' 
                      : '📍 Location detected — results near you'}
                  </span>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-3">
                {DESIRES.map((d, i) => (
                  <motion.button
                    key={d.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => selectDesire(d.id)}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                      desire === d.id
                        ? 'border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20'
                        : 'border-purple-100 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                    data-testid={`desire-${d.id}`}
                  >
                    <span className="text-4xl mb-2">{d.icon}</span>
                    <span className="text-sm font-semibold text-purple-800">
                      {d.label[language] || d.label.en}
                    </span>
                  </motion.button>
                ))}
              </div>

              {/* Quick actions */}
              <div className="mt-5 flex gap-2">
                <button
                  onClick={onScanHere}
                  data-testid="scan-here-btn"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold shadow-lg shadow-purple-500/30 active:scale-98"
                >
                  <Camera className="w-4 h-4" />
                  {language === 'fr' ? "C'est quoi ici ?" : "What's here?"}
                </button>
                <button
                  onClick={() => onAudioGuide && onAudioGuide({ type: 'quick_listen' })}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-100 text-purple-700 text-sm font-semibold active:scale-98 hover:bg-purple-200"
                >
                  <Headphones className="w-4 h-4" />
                  {language === 'fr' ? "Audio" : "Audio"}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 1.5: "Autres" Sub-options */}
          {showOtherOptions && (
            <motion.div
              key="other-options"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-5"
            >
              <p className="text-sm text-purple-600 mb-4 font-medium text-center">
                {language === 'fr' ? '✨ Choisis ton activité' : '✨ Choose your activity'}
              </p>
              
              <div className="grid grid-cols-3 gap-3">
                {OTHER_OPTIONS.map((opt, i) => (
                  <motion.button
                    key={opt.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => selectOtherOption(opt.id)}
                    className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-purple-100 hover:border-purple-400 hover:bg-purple-50/50 transition-all active:scale-95"
                    data-testid={`other-${opt.id}`}
                  >
                    <span className="text-3xl mb-2">{opt.icon}</span>
                    <span className="text-xs font-semibold text-purple-800">
                      {opt.label[language] || opt.label.en}
                    </span>
                    {opt.subOptions && (
                      <ChevronRight className="w-3 h-3 text-purple-400 mt-1" />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
          
          {/* Step 1.75: Sub-sub-options (sport type, shopping type, etc.) */}
          {showSubOptions && currentOtherOption && (
            <motion.div
              key="sub-options"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-5"
            >
              <p className="text-sm text-purple-600 mb-4 font-medium text-center">
                {currentOtherOption.question?.[language] || currentOtherOption.question?.fr || `${currentOtherOption.icon} ${currentOtherOption.label[language]}`}
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {currentOtherOption.subOptions.map((opt, i) => (
                  <motion.button
                    key={opt.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => selectSubOption(opt.id)}
                    className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-purple-100 hover:border-purple-400 hover:bg-purple-50/50 transition-all active:scale-95"
                    data-testid={`sub-${opt.id}`}
                  >
                    <span className="text-3xl mb-2">{opt.icon}</span>
                    <span className="text-xs font-semibold text-purple-800">
                      {opt.label[language] || opt.label.en}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Constraints */}
          {step === STEPS.CONSTRAINTS && (
            <motion.div
              key="constraints"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-5 space-y-5"
            >
              {/* Distance */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Navigation className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-purple-800">
                    {language === 'fr' ? 'Distance' : 'Distance'}
                  </span>
                </div>
                <div className="flex gap-2">
                  {CONSTRAINTS.distance.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => selectConstraint('distance', opt.id)}
                      className={`flex-1 flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all active:scale-95 ${
                        constraints.distance === opt.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-purple-100 hover:border-purple-300'
                      }`}
                    >
                      <span className="text-2xl mb-1">{opt.icon}</span>
                      <span className="text-xs font-medium text-purple-700">
                        {opt.label[language] || opt.label.en}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget - Dynamic options based on desire */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Euro className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-purple-800">
                    {language === 'fr' ? 'Budget' : 'Budget'}
                  </span>
                </div>
                <div className="flex gap-2">
                  {budgetOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => selectConstraint('budget', opt.id)}
                      className={`flex-1 flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all active:scale-95 ${
                        constraints.budget === opt.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-purple-100 hover:border-purple-300'
                      }`}
                    >
                      <span className="text-2xl mb-1">{opt.icon}</span>
                      <span className="text-xs font-medium text-purple-700">
                        {opt.label[language] || opt.label.en}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-purple-800">
                    {language === 'fr' ? 'Durée' : 'Duration'}
                  </span>
                </div>
                <div className="flex gap-2">
                  {CONSTRAINTS.time.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => selectConstraint('time', opt.id)}
                      className={`flex-1 flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all active:scale-95 ${
                        constraints.time === opt.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-purple-100 hover:border-purple-300'
                      }`}
                    >
                      <span className="text-2xl mb-1">{opt.icon}</span>
                      <span className="text-xs font-medium text-purple-700">
                        {opt.label[language] || opt.label.en}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate button */}
              <button
                onClick={generate}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold text-base shadow-xl shadow-purple-500/30 hover:shadow-2xl transition-all active:scale-98"
                data-testid="quick-deck-generate"
              >
                <Sparkles className="w-5 h-5" />
                {language === 'fr' ? 'Trouve-moi ça !' : 'Find me that!'}
              </button>
            </motion.div>
          )}

          {/* Step 3: Results */}
          {step === STEPS.RESULTS && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-5"
            >
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 animate-pulse" />
                    <Loader2 className="w-8 h-8 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin" />
                  </div>
                  <p className="mt-4 text-sm text-purple-600 font-medium animate-pulse">
                    {userLocation 
                      ? (language === 'fr' ? 'On cherche autour de vous...' : 'Searching nearby...') 
                      : (language === 'fr' ? 'On cherche les meilleures options...' : 'Finding the best options...')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Instant Tools - Translation & Conversion */}
                  <div className="mb-4">
                    <InstantTools />
                  </div>

                  {/* Location indicator + Open filter */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    {userLocation && (
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-medium">
                        <MapPin className="w-3 h-3" />
                        {language === 'fr' ? 'Autour de vous' : 'Near you'}
                      </div>
                    )}
                    
                    {/* Toggle "Ouvert actuellement" */}
                    <button
                      onClick={() => setFilterOpenOnly(!filterOpenOnly)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        filterOpenOnly 
                          ? 'bg-green-100 text-green-700 border border-green-300' 
                          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                      }`}
                      data-testid="filter-open-toggle"
                    >
                      {filterOpenOnly ? (
                        <ToggleRight className="w-4 h-4" />
                      ) : (
                        <ToggleLeft className="w-4 h-4" />
                      )}
                      {language === 'fr' ? 'Ouvert' : 'Open now'}
                    </button>
                  </div>
                  
                  {/* Results count */}
                  {filteredResults.length !== results.length && (
                    <p className="text-xs text-purple-500 mb-2">
                      {language === 'fr' 
                        ? `${filteredResults.length} lieu${filteredResults.length > 1 ? 'x' : ''} ouvert${filteredResults.length > 1 ? 's' : ''} sur ${results.length}`
                        : `${filteredResults.length} open place${filteredResults.length > 1 ? 's' : ''} of ${results.length}`}
                    </p>
                  )}
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {filteredResults.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-purple-600 font-medium">
                          {language === 'fr' ? 'Aucun lieu ouvert actuellement' : 'No places currently open'}
                        </p>
                        <button
                          onClick={() => setFilterOpenOnly(false)}
                          className="mt-2 text-sm text-purple-500 underline"
                        >
                          {language === 'fr' ? 'Voir tous les résultats' : 'Show all results'}
                        </button>
                      </div>
                    ) : filteredResults.map((result, index) => (
                      <motion.div
                        key={result.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-2xl border border-purple-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                      >
                        {/* Photo if available */}
                        {result.photo && (
                          <div className="h-28 bg-purple-100 overflow-hidden">
                            <img 
                              src={result.photo} 
                              alt={result.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-purple-900 truncate">{result.name}</h3>
                              
                              {/* Category/Type */}
                              {result.category && (
                                <span className="inline-block px-2 py-0.5 mt-1 rounded-md bg-purple-100 text-purple-700 text-[10px] font-medium uppercase">
                                  {result.category}
                                </span>
                              )}
                              
                              {/* Rating */}
                              {result.rating && (
                                <div className="flex items-center gap-1 mt-1.5">
                                  <div className="flex">
                                    {[1,2,3,4,5].map(i => (
                                      <span key={i} className={`text-xs ${i <= Math.round(result.rating) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                                    ))}
                                  </div>
                                  <span className="text-xs font-semibold text-purple-700">{result.rating}</span>
                                  {result.reviews_count && (
                                    <span className="text-xs text-purple-400">({result.reviews_count})</span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {result.open_now !== undefined && (
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                                result.open_now 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-50 text-red-600'
                              }`}>
                                {result.open_now 
                                  ? (language === 'fr' ? 'Ouvert' : 'Open')
                                  : (language === 'fr' ? 'Fermé' : 'Closed')}
                              </span>
                            )}
                          </div>
                          
                          {/* Address */}
                          {result.address && (
                            <p className="text-xs text-purple-500 mb-2 truncate">
                              📍 {result.address}
                            </p>
                          )}
                          
                          {/* Description */}
                          {result.description && (
                            <p className="text-xs text-purple-600 mb-3 line-clamp-2">
                              {result.description}
                            </p>
                          )}
                          
                          {/* Meta info */}
                          <div className="flex items-center gap-3 text-xs text-purple-500 mb-3">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {result.distance}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {result.duration}
                            </span>
                            {result.budget && <span>{result.budget}</span>}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                const mapsUrl = result.maps_url || 
                                  (result.lat && result.lng 
                                    ? `https://www.google.com/maps/dir/?api=1&destination=${result.lat},${result.lng}`
                                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(result.name)}`);
                                window.open(mapsUrl, '_blank');
                              }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-purple-600 text-white text-xs font-semibold active:scale-95"
                            >
                              <Navigation className="w-3.5 h-3.5" />
                              {language === 'fr' ? 'Y aller' : 'Go'}
                            </button>
                            <button 
                              onClick={() => handleQuickInfo(result)}
                              className="p-2.5 rounded-xl bg-purple-100 text-purple-600 active:scale-95 hover:bg-purple-200"
                              title={language === 'fr' ? 'Info rapide' : 'Quick info'}
                            >
                              <Info className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleAudioGuide(result)}
                              disabled={isGenerating}
                              className="p-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white active:scale-95 disabled:opacity-50"
                              title={language === 'fr' ? 'Audio Guide' : 'Audio Guide'}
                              data-testid={`audio-guide-btn-${result.id}`}
                            >
                              {isGenerating ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Headphones className="w-4 h-4" />
                              )}
                            </button>
                            <button 
                              onClick={() => handleToggleFavorite(result)}
                              disabled={savingFavorite === (result.place_id || result.id)}
                              className={`p-2.5 rounded-xl active:scale-95 transition-all ${
                                favoriteIds.has(result.place_id || result.id)
                                  ? 'bg-pink-500 text-white'
                                  : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
                              }`}
                              data-testid={`favorite-${result.id}`}
                            >
                              {savingFavorite === (result.place_id || result.id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : favoriteIds.has(result.place_id || result.id) ? (
                                <BookmarkCheck className="w-4 h-4" />
                              ) : (
                                <Bookmark className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Quick Info Popup */}
                  <AnimatePresence>
                    {selectedInfoResult && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:bottom-8 sm:left-1/2 sm:-translate-x-1/2 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-2xl border border-purple-200 p-4 z-10"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-purple-900 text-lg">{selectedInfoResult.name}</h4>
                            <p className="text-sm text-purple-500 mt-0.5">{selectedInfoResult.category}</p>
                          </div>
                          <button 
                            onClick={() => setSelectedInfoResult(null)}
                            className="p-1.5 rounded-lg hover:bg-purple-100 text-purple-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {selectedInfoResult.description && (
                          <p className="text-sm text-purple-700 mt-3 leading-relaxed">
                            {selectedInfoResult.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-purple-500">
                          {selectedInfoResult.address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {selectedInfoResult.address}
                            </span>
                          )}
                          {selectedInfoResult.rating && (
                            <span className="flex items-center gap-1">
                              ⭐ {selectedInfoResult.rating}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <button
                            onClick={() => {
                              const mapsUrl = selectedInfoResult.maps_url || 
                                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedInfoResult.name)}`;
                              window.open(mapsUrl, '_blank');
                            }}
                            className="flex-1 py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold active:scale-95"
                          >
                            {language === 'fr' ? 'Y aller' : 'Go there'}
                          </button>
                          <button
                            onClick={() => {
                              handleAudioGuide(selectedInfoResult);
                              setSelectedInfoResult(null);
                            }}
                            className="py-2.5 px-4 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-semibold active:scale-95 flex items-center gap-2"
                          >
                            <Headphones className="w-4 h-4" />
                            Audio
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={reset}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-purple-200 text-purple-700 font-semibold active:scale-95"
                    >
                      <RefreshCw className="w-4 h-4" />
                      {language === 'fr' ? 'Recommencer' : 'Start over'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Version Picker Overlay — appears when user taps Audio Guide */}
        <AnimatePresence>
          {versionPickerResult && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-purple-100">
                <div>
                  <h3 className="font-bold text-purple-900">Audio Guide</h3>
                  <p className="text-xs text-purple-500 truncate max-w-[220px]">{versionPickerResult.name}</p>
                </div>
                <button
                  onClick={() => setVersionPickerResult(null)}
                  className="p-2 rounded-xl hover:bg-purple-50 text-purple-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 p-5 flex flex-col justify-center gap-3">
                <p className="text-sm text-gray-600 text-center mb-2">
                  {language === 'fr' ? 'Choisissez la durée de votre visite :' : 'Choose your tour duration:'}
                </p>
                {[
                  { id: 'short', label: language === 'fr' ? 'Courte' : 'Short', minutes: 2, description: language === 'fr' ? "L'essentiel en 2 min" : 'Essentials in 2 min', chapters: 3, color: 'from-green-400 to-green-600', icon: '⚡' },
                  { id: 'medium', label: language === 'fr' ? 'Moyenne' : 'Medium', minutes: 5, description: language === 'fr' ? 'Visite complète en 5 min' : 'Complete tour in 5 min', chapters: 5, color: 'from-purple-500 to-violet-600', icon: '🎧' },
                  { id: 'long', label: language === 'fr' ? 'Longue' : 'Long', minutes: 10, description: language === 'fr' ? 'Immersion totale en 10 min' : 'Full immersion in 10 min', chapters: 7, color: 'from-rose-500 to-pink-600', icon: '🎭' },
                ].map(v => (
                  <button
                    key={v.id}
                    onClick={() => handleVersionSelect(v.id)}
                    disabled={isGenerating}
                    data-testid={`version-${v.id}`}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-purple-50 border-2 border-gray-100 hover:border-purple-300 hover:shadow-md transition-all group active:scale-98 disabled:opacity-50"
                  >
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br ${v.color} shadow-lg flex-shrink-0`}>
                      {v.icon}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900">{v.label}</span>
                        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                          ~{v.minutes} min
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{v.description}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {v.chapters} {language === 'fr' ? 'chapitres' : 'chapters'}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors flex-shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </motion.div>
    </motion.div>
  );
}

// Mock results for demo - enriched with more details
function getMockResults(desire, language, userLocation) {
  // Paris coordinates as default demo location
  const baseLat = userLocation?.lat || 48.8566;
  const baseLng = userLocation?.lng || 2.3522;
  
  const mockData = {
    visit: [
      { 
        id: 'v1', 
        name: language === 'fr' ? 'Musée d\'Orsay' : 'Orsay Museum', 
        category: language === 'fr' ? 'Musée' : 'Museum',
        address: '1 Rue de la Légion d\'Honneur, 75007 Paris',
        description: language === 'fr' 
          ? 'Ancienne gare transformée en musée, abritant la plus grande collection d\'art impressionniste au monde.'
          : 'Former railway station converted into a museum, housing the world\'s largest collection of Impressionist art.',
        distance: '1.2 km', 
        duration: '2h', 
        budget: '€€', 
        open_now: true, 
        rating: 4.7,
        reviews_count: 89420,
        photo: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=400&h=200&fit=crop',
        lat: baseLat + 0.008,
        lng: baseLng - 0.012
      },
      { 
        id: 'v2', 
        name: language === 'fr' ? 'Quartier du Marais' : 'Le Marais District', 
        category: language === 'fr' ? 'Quartier' : 'District',
        address: 'Le Marais, 75004 Paris',
        description: language === 'fr'
          ? 'Quartier historique avec hôtels particuliers, boutiques tendance et vie nocturne animée.'
          : 'Historic district with mansions, trendy boutiques and vibrant nightlife.',
        distance: '800m', 
        duration: '1h30', 
        budget: '€', 
        open_now: true,
        rating: 4.5,
        reviews_count: 15230,
        lat: baseLat + 0.003,
        lng: baseLng + 0.005
      },
      { 
        id: 'v3', 
        name: language === 'fr' ? 'Catacombes de Paris' : 'Paris Catacombs', 
        category: language === 'fr' ? 'Monument' : 'Landmark',
        address: '1 Avenue du Colonel Henri Rol-Tanguy, 75014 Paris',
        description: language === 'fr'
          ? 'Ossuaire souterrain contenant les restes de plus de six millions de personnes.'
          : 'Underground ossuary containing the remains of more than six million people.',
        distance: '3.5 km', 
        duration: '1h', 
        budget: '€€', 
        open_now: false,
        rating: 4.4,
        reviews_count: 42150,
        photo: 'https://images.unsplash.com/photo-1584467541268-b040f83be3fd?w=400&h=200&fit=crop',
        lat: baseLat - 0.025,
        lng: baseLng - 0.008
      },
    ],
    discover: [
      { 
        id: 'd1', 
        name: language === 'fr' ? 'Passage des Panoramas' : 'Panoramas Passage', 
        category: language === 'fr' ? 'Passage couvert' : 'Covered passage',
        address: '11 Boulevard Montmartre, 75002 Paris',
        description: language === 'fr'
          ? 'Le plus ancien passage couvert de Paris, construit en 1799. Boutiques vintage et restaurants.'
          : 'The oldest covered passage in Paris, built in 1799. Vintage shops and restaurants.',
        distance: '600m', 
        duration: '45m', 
        budget: '€', 
        open_now: true,
        rating: 4.3,
        reviews_count: 8920,
        lat: baseLat + 0.004,
        lng: baseLng + 0.002
      },
      { 
        id: 'd2', 
        name: language === 'fr' ? 'Promenade Plantée' : 'Promenade Plantée', 
        category: language === 'fr' ? 'Parc' : 'Park',
        address: '1 Coulée Verte René-Dumont, 75012 Paris',
        description: language === 'fr'
          ? 'Première promenade sur viaduc au monde, inspiratrice de la High Line de New York.'
          : 'The world\'s first elevated park walkway, which inspired New York\'s High Line.',
        distance: '2.1 km', 
        duration: '1h', 
        budget: '€', 
        open_now: true,
        rating: 4.6,
        reviews_count: 12450,
        photo: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=200&fit=crop',
        lat: baseLat - 0.012,
        lng: baseLng + 0.018
      },
    ],
    eat: [
      { 
        id: 'e1', 
        name: 'Bouillon Chartier', 
        category: 'Restaurant',
        address: '7 Rue du Faubourg Montmartre, 75009 Paris',
        description: language === 'fr'
          ? 'Institution parisienne depuis 1896. Cuisine française traditionnelle à petits prix dans un décor Belle Époque.'
          : 'Parisian institution since 1896. Traditional French cuisine at low prices in a Belle Époque setting.',
        distance: '500m', 
        duration: '45m', 
        budget: '€', 
        open_now: true,
        rating: 4.2,
        reviews_count: 35800,
        photo: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=200&fit=crop',
        lat: baseLat + 0.006,
        lng: baseLng - 0.002
      },
      { 
        id: 'e2', 
        name: 'Pink Mamma', 
        category: 'Italien',
        address: '20bis Rue de Douai, 75009 Paris',
        description: language === 'fr'
          ? 'Restaurant italien branché sur 4 étages avec décor végétal luxuriant.'
          : 'Trendy 4-story Italian restaurant with lush plant decor.',
        distance: '1.5 km', 
        duration: '1h30', 
        budget: '€€', 
        open_now: true,
        rating: 4.1,
        reviews_count: 18920,
        photo: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=200&fit=crop',
        lat: baseLat + 0.012,
        lng: baseLng - 0.008
      },
    ],
    drink: [
      { 
        id: 'dr1', 
        name: 'Le Perchoir', 
        category: 'Rooftop',
        address: '14 Rue Crespin du Gast, 75011 Paris',
        description: language === 'fr'
          ? 'Bar rooftop avec vue panoramique sur Paris. Cocktails créatifs et ambiance décontractée.'
          : 'Rooftop bar with panoramic views of Paris. Creative cocktails and relaxed atmosphere.',
        distance: '1.8 km', 
        duration: '1h', 
        budget: '€€', 
        open_now: true,
        rating: 4.3,
        reviews_count: 7850,
        photo: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=200&fit=crop',
        lat: baseLat + 0.008,
        lng: baseLng + 0.022
      },
      { 
        id: 'dr2', 
        name: 'Candelaria', 
        category: 'Speakeasy',
        address: '52 Rue de Saintonge, 75003 Paris',
        description: language === 'fr'
          ? 'Bar caché derrière une taqueria. Meilleurs cocktails de Paris dans une ambiance intimiste.'
          : 'Hidden bar behind a taqueria. Best cocktails in Paris in an intimate atmosphere.',
        distance: '900m', 
        duration: '1h30', 
        budget: '€€', 
        open_now: true,
        rating: 4.5,
        reviews_count: 4320,
        lat: baseLat + 0.005,
        lng: baseLng + 0.008
      },
    ],
    party: [
      { 
        id: 'p1', 
        name: 'Rex Club', 
        category: language === 'fr' ? 'Club' : 'Nightclub',
        address: '5 Boulevard Poissonnière, 75002 Paris',
        description: language === 'fr'
          ? 'Temple de la techno parisienne depuis 1988. Système son légendaire et DJs internationaux.'
          : 'Temple of Parisian techno since 1988. Legendary sound system and international DJs.',
        distance: '700m', 
        duration: '4h', 
        budget: '€€', 
        open_now: true,
        rating: 4.4,
        reviews_count: 3890,
        photo: 'https://images.unsplash.com/photo-1571266028243-e4733b0f0bb0?w=400&h=200&fit=crop',
        lat: baseLat + 0.005,
        lng: baseLng + 0.001
      },
    ],
    chill: [
      { 
        id: 'c1', 
        name: language === 'fr' ? 'Square du Vert-Galant' : 'Vert-Galant Square', 
        category: language === 'fr' ? 'Parc' : 'Park',
        address: 'Place du Pont Neuf, 75001 Paris',
        description: language === 'fr'
          ? 'Jardin paisible à la pointe de l\'Île de la Cité avec vue sur la Seine et le Pont Neuf.'
          : 'Peaceful garden at the tip of Île de la Cité with views of the Seine and Pont Neuf.',
        distance: '400m', 
        duration: '1h', 
        budget: '€', 
        open_now: true,
        rating: 4.7,
        reviews_count: 6780,
        photo: 'https://images.unsplash.com/photo-1431274172761-fca41d930114?w=400&h=200&fit=crop',
        lat: baseLat - 0.001,
        lng: baseLng - 0.003
      },
      { 
        id: 'c2', 
        name: 'Café de Flore', 
        category: 'Café',
        address: '172 Boulevard Saint-Germain, 75006 Paris',
        description: language === 'fr'
          ? 'Café mythique fréquenté par Sartre et Beauvoir. Terrasse emblématique de Saint-Germain-des-Prés.'
          : 'Legendary café frequented by Sartre and Beauvoir. Iconic terrace of Saint-Germain-des-Prés.',
        distance: '1.2 km', 
        duration: '1h', 
        budget: '€€', 
        open_now: true,
        rating: 4.1,
        reviews_count: 21340,
        photo: 'https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=400&h=200&fit=crop',
        lat: baseLat - 0.008,
        lng: baseLng - 0.012
      },
    ],
  };
  return mockData[desire] || mockData.discover;
}

export default QuickDeckGame;
